/**
 * ProjectFiles Component
 * Manages project file uploads with drag & drop, listing, and previews
 */
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card } from '../ui/card';
import { Image, FileText, Download, Paperclip, Upload, X, Edit2, Check, Trash2 } from 'lucide-react';
import { API, getApiUrl } from '../../utils/api';
import { getFullFileUrl } from '../../utils/fileUtils';
import { DebugUrlDisplay } from '../debug';

const ProjectFiles = ({ project, currentUser, onFilePreview }) => {
  const { t } = useTranslation();
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isDragOver, setIsDragOver] = useState(false);
  const [editingFileId, setEditingFileId] = useState(null);
  const [editDescription, setEditDescription] = useState('');
  const [pasteHint, setPasteHint] = useState(false);
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);

  useEffect(() => {
    if (project) {
      loadProjectFiles();
    }
  }, [project?.id]);

  // Handle paste from clipboard (screenshots, copied images)
  useEffect(() => {
    const handlePaste = async (e) => {
      // Check if we have image data in clipboard
      const items = e.clipboardData?.items;
      if (!items) return;

      for (const item of items) {
        if (item.type.startsWith('image/')) {
          e.preventDefault();
          const blob = item.getAsFile();
          if (blob) {
            // Create a file from blob with timestamp name
            const extension = item.type.split('/')[1] || 'png';
            const fileName = `screenshot_${new Date().toISOString().replace(/[:.]/g, '-')}.${extension}`;
            const file = new File([blob], fileName, { type: item.type });
            
            // Show feedback
            setPasteHint(true);
            setTimeout(() => setPasteHint(false), 2000);
            
            await uploadFile(file);
          }
          break;
        }
      }
    };

    // Add paste listener to document
    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [project?.id, currentUser?.id]);

  const loadProjectFiles = async () => {
    setLoading(true);
    try {
      const timestamp = Date.now();
      const response = await axios.get(`${getApiUrl()}/projects/${project.id}/files?_t=${timestamp}`);
      
      const validFiles = response.data.filter(file => {
        if (file.file_exists === false) {
          console.warn(`⚠️ File no longer exists: ${file.original_filename}`);
          return false;
        }
        return true;
      });
      
      setFiles(validFiles);
    } catch (error) {
      console.error('Error loading project files:', error);
      setFiles([]);
    } finally {
      setLoading(false);
    }
  };
  
  const validateAndOpenFile = async (fileUrl, fileName, fileType, originalUrl = null) => {
    try {
      const response = await axios.get(`${getApiUrl()}/validate/file?file_path=${encodeURIComponent(fileUrl)}&_t=${Date.now()}`);
      
      if (!response.data.is_valid) {
        const errorMsg = response.data.error === 'FILE_NOT_IN_DATABASE' 
          ? t('files.notInDatabase', '⚠️ This file does not exist in the current database.\n\nCached data from an old fork may be displayed.\n\nPlease reload the page (Ctrl+Shift+R).')
          : t('files.notFound', '⚠️ The file could not be found.');
        
        alert(errorMsg);
        loadProjectFiles();
        return;
      }
      
      onFilePreview && onFilePreview(fileUrl, fileName, fileType, originalUrl);
    } catch (error) {
      console.error('Validation error:', error);
      onFilePreview && onFilePreview(fileUrl, fileName, fileType, originalUrl);
    }
  };

  const uploadFile = async (file) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      await axios.post(
        `${getApiUrl()}/upload/project`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          params: {
            project_id: project.id,
            uploader_id: currentUser.id,
            description: ''
          }
        }
      );

      loadProjectFiles();
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Fehler beim Hochladen der Datei: ' + (error.response?.data?.detail || error.message));
    } finally {
      setUploading(false);
    }
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    await uploadFile(file);
    event.target.value = '';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length > 0) {
      // Upload files one by one
      for (const file of droppedFiles) {
        await uploadFile(file);
      }
    }
  };

  const startEditDescription = (file) => {
    setEditingFileId(file.id);
    setEditDescription(file.description || '');
  };

  const cancelEditDescription = () => {
    setEditingFileId(null);
    setEditDescription('');
  };

  const saveDescription = async (fileId) => {
    try {
      await axios.put(`${getApiUrl()}/files/${fileId}/description`, null, {
        params: { description: editDescription }
      });
      
      // Update local state
      setFiles(files.map(f => 
        f.id === fileId ? { ...f, description: editDescription } : f
      ));
      setEditingFileId(null);
      setEditDescription('');
    } catch (error) {
      console.error('Error updating description:', error);
      alert('Fehler beim Speichern der Beschreibung');
    }
  };

  const deleteFile = async (fileId, fileName) => {
    const confirmMessage = t('files.confirmDelete', 'Are you sure you want to delete "{{fileName}}"?').replace('{{fileName}}', fileName);
    
    if (!window.confirm(confirmMessage)) {
      return;
    }
    
    try {
      await axios.delete(`${getApiUrl()}/projects/${project.id}/files/${fileId}`, {
        params: { user_id: currentUser.id }
      });
      
      // Remove from local state
      setFiles(files.filter(f => f.id !== fileId));
    } catch (error) {
      console.error('Error deleting file:', error);
      const errorMsg = error.response?.data?.detail || error.message;
      alert(t('files.deleteError', 'Error deleting file: {{error}}').replace('{{error}}', errorMsg));
    }
  };

  const getFileIcon = (fileType) => {
    switch (fileType) {
      case 'image':
        return <Image className="w-5 h-5 text-blue-500" />;
      case 'pdf':
        return <FileText className="w-5 h-5 text-red-500" />;
      case 'document':
        return <FileText className="w-5 h-5 text-blue-600" />;
      case 'spreadsheet':
        return <FileText className="w-5 h-5 text-green-600" />;
      case 'presentation':
        return <FileText className="w-5 h-5 text-orange-600" />;
      default:
        return <FileText className="w-5 h-5 text-gray-500" />;
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <h3 className="font-semibold text-gray-800 mb-4">{t('files.projectFiles', 'Project files')}</h3>
        
        {/* Drag & Drop Upload Area */}
        <Card 
          className={`p-6 mb-4 border-2 border-dashed transition-all cursor-pointer ${
            isDragOver 
              ? 'border-emerald-500 bg-emerald-50' 
              : 'border-gray-300 bg-gray-50 hover:border-emerald-400 hover:bg-emerald-50/50'
          } ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => !uploading && fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileSelect}
            className="hidden"
            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.dxf"
            disabled={uploading}
          />
          
          <div className="text-center">
            {uploading ? (
              <>
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-3"></div>
                <p className="text-emerald-700 font-medium">{t('files.uploading', 'Uploading...')}</p>
              </>
            ) : (
              <>
                <Upload className={`w-12 h-12 mx-auto mb-3 ${isDragOver ? 'text-emerald-600' : 'text-gray-400'}`} />
                <p className="text-gray-700 font-medium mb-1">
                  {isDragOver ? t('files.dropHere', 'Drop file here') : t('files.dragHere', 'Drag file here')}
                </p>
                <p className="text-gray-500 text-sm">
                  {t('files.or', 'or')} <span className="text-emerald-600 font-medium">{t('files.clickToSelect', 'click to select')}</span>
                </p>
                <p className="text-gray-400 text-xs mt-2">
                  {t('files.supportedFormats', 'Images, PDF, Word, Excel, PowerPoint, DXF')}
                </p>
                <p className="text-blue-500 text-xs mt-1 font-medium">
                  💡 {t('files.pasteHint', 'Ctrl+V / Cmd+V zum Einfügen von Screenshots')}
                </p>
              </>
            )}
          </div>
        </Card>

        {/* Paste Success Notification */}
        {pasteHint && (
          <div className="fixed bottom-4 right-4 bg-emerald-600 text-white px-4 py-2 rounded-lg shadow-lg z-50 animate-pulse">
            📋 {t('files.pasteSuccess', 'Bild aus Zwischenablage wird hochgeladen...')}
          </div>
        )}
        
        {/* File List */}
        <div className="space-y-3">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
              <span className="ml-3 text-gray-500">{t('files.loading', 'Loading files...')}</span>
            </div>
          ) : files.length === 0 ? (
            <p className="text-gray-500 text-center py-8">Noch keine Dateien hochgeladen</p>
          ) : (
            files.map(file => (
              <div key={file.id} className="p-3 bg-white rounded-lg shadow-sm border hover:bg-gray-50 transition-colors">
                <div className="flex items-start">
                  <div className="mr-3 mt-1">
                    {getFileIcon(file.file_type)}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div 
                      className="cursor-pointer" 
                      onClick={() => {
                        const previewUrl = file.preview_file_path ? getFullFileUrl(file.preview_file_path) : getFullFileUrl(file.file_path);
                        const fileType = file.preview_file_path ? 'pdf' : file.file_type;
                        const originalUrl = file.preview_file_path ? getFullFileUrl(file.file_path) : null;
                        validateAndOpenFile(previewUrl, file.original_filename, fileType, originalUrl);
                      }}
                    >
                      <p className="font-medium text-gray-800 truncate hover:text-emerald-600 transition-colors">
                        {file.original_filename}
                        {file.preview_file_path && (
                          <span className="text-xs text-emerald-600 ml-2">✓ PDF-Vorschau</span>
                        )}
                      </p>
                    </div>
                    
                    {/* Editable Description */}
                    {editingFileId === file.id ? (
                      <div className="flex items-center gap-2 mt-2">
                        <Input
                          value={editDescription}
                          onChange={(e) => setEditDescription(e.target.value)}
                          placeholder={t('files.addDescription', 'Add description...')}
                          className="flex-1 h-8 text-sm"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') saveDescription(file.id);
                            if (e.key === 'Escape') cancelEditDescription();
                          }}
                        />
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => saveDescription(file.id)}>
                          <Check className="w-4 h-4 text-emerald-600" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={cancelEditDescription}>
                          <X className="w-4 h-4 text-gray-500" />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 mt-1">
                        {file.description ? (
                          <p className="text-sm text-gray-600 truncate flex-1">
                            {file.description}
                          </p>
                        ) : (
                          <p className="text-sm text-gray-400 italic flex-1">
                            {t('files.noDescription', 'Keine Beschreibung')}
                          </p>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            startEditDescription(file);
                          }}
                          className="p-1 hover:bg-gray-100 rounded text-gray-400 hover:text-emerald-600 transition-colors shrink-0"
                          title={t('files.editDescription', 'Beschreibung bearbeiten')}
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                    
                    <div className="flex items-center gap-4 text-xs text-gray-500 mt-1">
                      <span>{t('files.uploadedBy', 'From')}: {file.uploader_name}</span>
                      <span>{new Date(file.uploaded_at).toLocaleDateString(currentUser?.language || 'de')}</span>
                      {file.file_size && (
                        <span>{Math.round(file.file_size / 1024)} KB</span>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 ml-2">
                    {file.file_type === 'image' && (
                      <img 
                        src={getFullFileUrl(file.file_path)} 
                        alt={file.original_filename}
                        className="w-12 h-12 object-cover rounded cursor-pointer hover:opacity-80 transition-opacity"
                        onClick={() => onFilePreview && onFilePreview(getFullFileUrl(file.file_path), file.original_filename, 'image')}
                      />
                    )}
                    
                    <a 
                      href={getFullFileUrl(file.file_path)} 
                      download={file.original_filename}
                      className="p-2 hover:bg-gray-100 rounded"
                      onClick={(e) => e.stopPropagation()}
                      title={t('files.download', 'Download')}
                    >
                      <Download className="w-4 h-4 text-gray-600" />
                    </a>
                    
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteFile(file.id, file.original_filename);
                      }}
                      className="p-2 hover:bg-red-50 rounded text-gray-400 hover:text-red-500 transition-colors"
                      title={t('files.delete', 'Delete')}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                
                <DebugUrlDisplay url={getFullFileUrl(file.file_path)} label="Datei-URL" />
                {file.preview_file_path && <DebugUrlDisplay url={getFullFileUrl(file.preview_file_path)} label="Preview-URL" />}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectFiles;
